import React, { useCallback, useEffect, useMemo, useState } from 'react';
import {
  Accordion,
  AccordionDetails,
  AccordionSummary,
  Alert,
  Box,
  Button,
  Checkbox,
  Chip,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogContentText,
  DialogTitle,
  FormControlLabel,
  Typography,
} from '@mui/material';
import { ExpandMore as ExpandMoreIcon } from '@mui/icons-material';
import { pollBackfillIngest, startBackfillIngest } from '../../api/contentCompletion';
import {
  BackfillFeedType,
  BackfillIngestResult,
  BackfillPreviewResult,
  CompanyContentHealth,
} from '../../types/contentCompletion';
import { useNotification } from '../../context/NotificationContext';
import { useEnvironment } from '../../hooks/useEnvironment';
import BackfillReviewTable, { defaultSelectedUrls, isIngestiblePreviewItem } from './BackfillReviewTable';

export interface BatchBackfillReviewItem {
  companyId: string;
  companyName: string;
  company: CompanyContentHealth;
  sourceUrl: string;
  previewResult: BackfillPreviewResult;
}

interface BatchBackfillReviewDialogProps {
  open: boolean;
  onClose: () => void;
  items: BatchBackfillReviewItem[];
  daysBack: number;
  feedType: BackfillFeedType;
  onCompanyIngested: (companyId: string) => void;
  onComplete: () => void;
}

const pollIngestToCompletion = (runId: string): Promise<BackfillIngestResult> =>
  new Promise((resolve) => {
    const stop = pollBackfillIngest(runId, (update) => {
      if (update.status !== 'running') {
        stop();
        resolve(update);
      }
    });
  });

const BatchBackfillReviewDialog: React.FC<BatchBackfillReviewDialogProps> = ({
  open,
  onClose,
  items,
  daysBack,
  feedType,
  onCompanyIngested,
  onComplete,
}) => {
  const { showNotification } = useNotification();
  const { environment } = useEnvironment();
  const [selectedUrlsByCompany, setSelectedUrlsByCompany] = useState<Record<string, Set<string>>>(
    {}
  );
  const [savedCompanyIds, setSavedCompanyIds] = useState<Set<string>>(new Set());
  const [expandedCompanyId, setExpandedCompanyId] = useState<string | false>(false);
  const [skipSummaries, setSkipSummaries] = useState(false);
  const [skipLabelling, setSkipLabelling] = useState(false);
  const [ingesting, setIngesting] = useState(false);
  const [ingestProgress, setIngestProgress] = useState<string | null>(null);
  const [confirmProductionOpen, setConfirmProductionOpen] = useState(false);
  const [productionConfirmed, setProductionConfirmed] = useState(false);
  const [pendingSaveAll, setPendingSaveAll] = useState(false);
  const [singleSaveItem, setSingleSaveItem] = useState<BatchBackfillReviewItem | null>(null);

  useEffect(() => {
    if (!open) return;
    const next: Record<string, Set<string>> = {};
    for (const item of items) {
      next[item.companyId] = defaultSelectedUrls(item.previewResult.items);
    }
    setSelectedUrlsByCompany(next);
    setSavedCompanyIds(new Set());
    setExpandedCompanyId(items[0]?.companyId ?? false);
    setSkipSummaries(false);
    setSkipLabelling(false);
    setIngestProgress(null);
    setConfirmProductionOpen(false);
    setProductionConfirmed(false);
    setPendingSaveAll(false);
    setSingleSaveItem(null);
  }, [open, items]);

  const pendingItems = useMemo(
    () => items.filter((item) => !savedCompanyIds.has(item.companyId)),
    [items, savedCompanyIds]
  );

  const totalSelectedCount = useMemo(
    () =>
      pendingItems.reduce((sum, item) => {
        const selected = selectedUrlsByCompany[item.companyId];
        return sum + (selected?.size ?? 0);
      }, 0),
    [pendingItems, selectedUrlsByCompany]
  );

  const toggleUrl = useCallback((companyId: string, url: string) => {
    setSelectedUrlsByCompany((prev) => {
      const current = new Set(prev[companyId] ?? []);
      if (current.has(url)) {
        current.delete(url);
      } else {
        current.add(url);
      }
      return { ...prev, [companyId]: current };
    });
  }, []);

  const toggleSelectAll = useCallback((companyId: string, previewItems: BatchBackfillReviewItem['previewResult']['items']) => {
    setSelectedUrlsByCompany((prev) => {
      const selectable = previewItems
        .filter(isIngestiblePreviewItem)
        .map((item) => item.url as string);
      const current = prev[companyId] ?? new Set<string>();
      const allSelected =
        selectable.length > 0 && selectable.every((url) => current.has(url));
      return {
        ...prev,
        [companyId]: allSelected ? new Set<string>() : new Set(selectable),
      };
    });
  }, []);

  const ingestCompany = async (item: BatchBackfillReviewItem): Promise<BackfillIngestResult> => {
    const selected = selectedUrlsByCompany[item.companyId];
    if (!selected || selected.size === 0) {
      throw new Error('No press releases selected');
    }

    const { run_id } = await startBackfillIngest({
      company_id: item.companyId,
      company_name: item.companyName,
      source_url: item.sourceUrl.trim(),
      feed_type: feedType,
      days_back: daysBack,
      fetch_content: true,
      skip_summaries: skipSummaries,
      skip_labelling: skipLabelling,
      preview_run_id: item.previewResult.run_id,
      selected_urls: Array.from(selected),
    });

    return pollIngestToCompletion(run_id);
  };

  const markSaved = (companyId: string) => {
    setSavedCompanyIds((prev) => new Set(prev).add(companyId));
    onCompanyIngested(companyId);
    onComplete();
  };

  const handleSaveCompany = async (item: BatchBackfillReviewItem) => {
    const selected = selectedUrlsByCompany[item.companyId];
    if (!selected || selected.size === 0) {
      showNotification('Select at least one press release to save', 'warning');
      return;
    }

    setIngesting(true);
    setIngestProgress(item.companyName);
    try {
      const result = await ingestCompany(item);
      if (result.status === 'success') {
        showNotification(
          `${item.companyName}: ${result.created} created, ${result.skipped_existing} skipped`,
          result.errors > 0 ? 'warning' : 'success'
        );
        markSaved(item.companyId);
      } else {
        showNotification(result.error || `Failed to save ${item.companyName}`, 'error');
      }
    } catch (err: unknown) {
      const msg =
        (err as { response?: { data?: { detail?: string } } })?.response?.data?.detail ||
        (err as Error)?.message ||
        `Failed to save ${item.companyName}`;
      showNotification(msg, 'error');
    } finally {
      setIngesting(false);
      setIngestProgress(null);
    }
  };

  const runSaveAll = async () => {
    const toSave = pendingItems.filter(
      (item) => (selectedUrlsByCompany[item.companyId]?.size ?? 0) > 0
    );
    if (toSave.length === 0) {
      showNotification('No press releases selected to save', 'warning');
      return;
    }

    setIngesting(true);
    let saved = 0;
    let failed = 0;
    let articlesCreated = 0;
    let articlesSkipped = 0;

    for (let i = 0; i < toSave.length; i += 1) {
      const item = toSave[i];
      setIngestProgress(`${item.companyName} (${i + 1}/${toSave.length})`);
      try {
        const result = await ingestCompany(item);
        if (result.status === 'success') {
          markSaved(item.companyId);
          saved += 1;
          articlesCreated += result.created || 0;
          articlesSkipped += result.skipped_existing || 0;
        } else {
          failed += 1;
          showNotification(result.error || `Failed to save ${item.companyName}`, 'error');
        }
      } catch (err: unknown) {
        failed += 1;
        const msg =
          (err as { response?: { data?: { detail?: string } } })?.response?.data?.detail ||
          (err as Error)?.message ||
          `Failed to save ${item.companyName}`;
        showNotification(msg, 'error');
      }
    }

    setIngesting(false);
    setIngestProgress(null);

    if (saved > 0) {
      showNotification(
        `Filled ${saved} compan${saved === 1 ? 'y' : 'ies'} · ${articlesCreated} article${
          articlesCreated === 1 ? '' : 's'
        } created${articlesSkipped > 0 ? ` · ${articlesSkipped} skipped` : ''}${
          failed > 0 ? ` (${failed} failed)` : ''
        }`,
        failed > 0 ? 'warning' : 'success'
      );
    }
  };

  const handleSaveAll = () => {
    if (environment === 'production') {
      setProductionConfirmed(false);
      setSingleSaveItem(null);
      setPendingSaveAll(true);
      setConfirmProductionOpen(true);
      return;
    }
    runSaveAll();
  };

  const handleSaveCompanyWithProductionCheck = (item: BatchBackfillReviewItem) => {
    if (environment === 'production') {
      setProductionConfirmed(false);
      setPendingSaveAll(false);
      setSingleSaveItem(item);
      setConfirmProductionOpen(true);
      return;
    }
    handleSaveCompany(item);
  };

  const handleConfirmProduction = async () => {
    if (!productionConfirmed) return;
    setConfirmProductionOpen(false);
    if (pendingSaveAll) {
      setPendingSaveAll(false);
      await runSaveAll();
    } else if (singleSaveItem) {
      const item = singleSaveItem;
      setSingleSaveItem(null);
      await handleSaveCompany(item);
    }
  };

  const handleClose = () => {
    if (ingesting) return;
    onClose();
  };

  return (
    <>
      <Dialog open={open} onClose={handleClose} maxWidth="lg" fullWidth>
        <DialogTitle>
          Review batch backfill — {items.length} compan{items.length === 1 ? 'y' : 'ies'}
        </DialogTitle>
        <DialogContent>
          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2, pt: 1 }}>
            {environment === 'production' && (
              <Alert severity="warning">
                You are in <strong>production</strong> mode. Saving will write to the live Neo4j
                database.
              </Alert>
            )}

            <Box sx={{ display: 'flex', gap: 2, flexWrap: 'wrap', alignItems: 'center' }}>
              <FormControlLabel
                control={
                  <Checkbox
                    checked={!skipSummaries}
                    onChange={(e) => setSkipSummaries(!e.target.checked)}
                    disabled={ingesting}
                  />
                }
                label="Generate summaries"
              />
              <FormControlLabel
                control={
                  <Checkbox
                    checked={!skipLabelling}
                    onChange={(e) => setSkipLabelling(!e.target.checked)}
                    disabled={ingesting}
                  />
                }
                label="Run content labelling"
              />
              <Typography variant="body2" color="text.secondary" sx={{ ml: 'auto' }}>
                {totalSelectedCount} PR(s) selected across {pendingItems.length} pending
              </Typography>
            </Box>

            {ingesting && (
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                <CircularProgress size={22} />
                <Typography variant="body2" color="text.secondary">
                  Saving to Neo4j{ingestProgress ? ` — ${ingestProgress}` : '…'}
                </Typography>
              </Box>
            )}

            {items.map((item) => {
              const isSaved = savedCompanyIds.has(item.companyId);
              const selected = selectedUrlsByCompany[item.companyId] ?? new Set<string>();
              const selectedCount = selected.size;
              const preview = item.previewResult;

              return (
                <Accordion
                  key={item.companyId}
                  expanded={expandedCompanyId === item.companyId}
                  onChange={(_, expanded) =>
                    setExpandedCompanyId(expanded ? item.companyId : false)
                  }
                  disabled={ingesting}
                  sx={{ opacity: isSaved ? 0.7 : 1 }}
                >
                  <AccordionSummary expandIcon={<ExpandMoreIcon />}>
                    <Box
                      sx={{
                        display: 'flex',
                        alignItems: 'center',
                        gap: 1,
                        flexWrap: 'wrap',
                        width: '100%',
                        pr: 1,
                      }}
                    >
                      <Typography fontWeight={600}>{item.companyName}</Typography>
                      <Typography variant="body2" color="text.secondary">
                        {preview.press_releases_found} found · {selectedCount} selected
                      </Typography>
                      {isSaved && <Chip label="Saved" color="success" size="small" />}
                    </Box>
                  </AccordionSummary>
                  <AccordionDetails>
                    <Typography variant="caption" color="text.secondary" display="block" sx={{ mb: 1 }}>
                      Source: {item.sourceUrl}
                      {preview.feed_type_used ? ` · ${preview.feed_type_used}` : ''}
                    </Typography>
                    <BackfillReviewTable
                      items={preview.items}
                      selectedUrls={selected}
                      onToggleUrl={(url) => toggleUrl(item.companyId, url)}
                      onToggleSelectAll={() => toggleSelectAll(item.companyId, preview.items)}
                      disabled={ingesting || isSaved}
                    />
                    {!isSaved && (
                      <Box sx={{ display: 'flex', justifyContent: 'flex-end', mt: 1 }}>
                        <Button
                          variant="outlined"
                          size="small"
                          disabled={ingesting || selectedCount === 0}
                          onClick={() => handleSaveCompanyWithProductionCheck(item)}
                        >
                          Save this company ({selectedCount})
                        </Button>
                      </Box>
                    )}
                  </AccordionDetails>
                </Accordion>
              );
            })}
          </Box>
        </DialogContent>
        <DialogActions sx={{ px: 3, pb: 2, flexWrap: 'wrap', gap: 1 }}>
          <Button onClick={handleClose} disabled={ingesting}>
            {pendingItems.length === 0 ? 'Done' : 'Close'}
          </Button>
          <Box sx={{ flex: 1 }} />
          <Button
            variant="contained"
            onClick={handleSaveAll}
            disabled={ingesting || totalSelectedCount === 0 || pendingItems.length === 0}
          >
            {ingesting
              ? 'Saving…'
              : `Save all selected (${totalSelectedCount} PRs, ${pendingItems.length} companies)`}
          </Button>
        </DialogActions>
      </Dialog>

      <Dialog
        open={confirmProductionOpen}
        onClose={() => {
          setConfirmProductionOpen(false);
          setPendingSaveAll(false);
          setSingleSaveItem(null);
        }}
      >
        <DialogTitle>Confirm production ingest</DialogTitle>
        <DialogContent>
          <DialogContentText sx={{ mb: 2 }}>
            {pendingSaveAll ? (
              <>
                You are about to write press releases for{' '}
                <strong>
                  {pendingItems.filter((i) => (selectedUrlsByCompany[i.companyId]?.size ?? 0) > 0)
                    .length}{' '}
                  companies
                </strong>{' '}
                to the <strong>production</strong> Neo4j database.
              </>
            ) : (
              <>
                You are about to write press releases for{' '}
                <strong>{singleSaveItem?.companyName}</strong> to the{' '}
                <strong>production</strong> Neo4j database.
              </>
            )}
          </DialogContentText>
          <FormControlLabel
            control={
              <Checkbox
                checked={productionConfirmed}
                onChange={(e) => setProductionConfirmed(e.target.checked)}
              />
            }
            label="I understand this will modify production data"
          />
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setConfirmProductionOpen(false)}>Cancel</Button>
          <Button
            variant="contained"
            color="error"
            onClick={handleConfirmProduction}
            disabled={!productionConfirmed}
          >
            Save to production
          </Button>
        </DialogActions>
      </Dialog>
    </>
  );
};

export default BatchBackfillReviewDialog;
